import { c as _c } from "react/compiler-runtime";
import { Checkbox, FormInput } from '@systemeio/ui';
import { createLecture } from 'modules/courses/courses/curriculum/api/lecture-api';
import { useLectureTemplate } from 'modules/courses/courses/curriculum/hooks/use-lecture-templates';
import { CourseCurriculumInterface } from 'modules/courses/courses/curriculum/types/curriculum-interface';
import { CreateLectureErrorsInterface, CreateLectureInterface, LectureTemplateInterface } from 'modules/courses/courses/curriculum/types/lecture-interface';
import { memo, useEffect, useState } from 'react';
import CreateModal from 'shared/components/create-modal';
import { FieldErrorAndDescription, FieldErrorAndDescriptionProps } from 'shared/components/form/field-error-and-description';
import { FieldLabel, FieldLabelProps } from 'shared/components/form/field-label';
import { Loader } from 'shared/components/loader';
import { TemplateCard } from 'shared/components/template-card';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRevalidateModules } from '@systemeio/util';
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors';
import { KeyedMutator } from 'swr';
import { AddLectureButton } from './add-lecture-button';
const defaultErrors: CreateLectureErrorsInterface = {
  fields: {
    name: '',
    pageTemplate: '',
    delayBeforePreviousLecture: ''
  }
};
const defaultValues: CreateLectureInterface = {
  name: '',
  delayBeforePreviousLecture: 0,
  showComments: false,
  pageTemplate: undefined
};
interface CreateLectureModalInterface {
  moduleId: number;
  mutate: KeyedMutator<CourseCurriculumInterface>;
}
const CreateLectureModal = t0 => {
  const $ = _c(38);
  const {
    moduleId,
    mutate
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [errors, setErrors] = useState(defaultErrors);
  const {
    revalidateModules
  } = useRevalidateModules();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setErrors(defaultErrors);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const afterLeave = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOpened(false);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onClose = t2;
  let t3;
  if ($[2] !== moduleId || $[3] !== mutate || $[4] !== revalidateModules) {
    t3 = async data => {
      const lectureData = {
        ...data,
        delayBeforePreviousLecture: data.delayBeforePreviousLecture ? data.delayBeforePreviousLecture : 0
      };
      await fetchWithFieldErrors(async () => {
        const response = await createLecture(moduleId, lectureData);
        await mutate(data_0 => {
          if (data_0) {
            return {
              ...data_0,
              modules: data_0.modules.map(module => {
                if (module.id === moduleId) {
                  return {
                    ...module,
                    lectures: [...module.lectures, response.data]
                  };
                }
                return module;
              })
            };
          }
        }, false);
      }, setErrors);
      await revalidateModules([RequestModuleEnum.MembershipCourseLecture]);
    };
    $[2] = moduleId;
    $[3] = mutate;
    $[4] = revalidateModules;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onCreate = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AddLectureButton onClick={() => setOpened(true)} data-test="module-create-lecture" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("dashboard.course.lecture.create");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("dashboard.course.lecture_title");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== errors || $[12] !== moduleId || $[13] !== t) {
    t7 = {
      field: "name",
      onRender: (value, onChange) => <FormInput key={`${moduleId}-name`} label={t("global.name")} value={value} required={true} onChange={e => onChange(e.target.value)} error={errors.fields.name} data-test="module-create-lecture-name" />
    };
    $[11] = errors;
    $[12] = moduleId;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== errors || $[16] !== moduleId || $[17] !== t) {
    t8 = {
      field: "delayBeforePreviousLecture",
      onRender: (value_0, onChange_0) => <FormInput key={`${moduleId}-delayBeforePreviousLecture`} label={t("dashboard.course.lecture_delay_title")} value={value_0} type="number" onChange={e_0 => onChange_0(Number(e_0.target.value))} error={errors.fields.delayBeforePreviousLecture} data-test="module-create-lecture-delay" />
    };
    $[15] = errors;
    $[16] = moduleId;
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== moduleId || $[20] !== t) {
    t9 = {
      field: "showComments",
      onRender: (value_1, onChange_1) => <Checkbox key={`${moduleId}-showComments`} label={t("dashboard.course.show_comments_title")} value={value_1} onChange={onChange_1} data-test="module-create-lecture-showComments" />
    };
    $[19] = moduleId;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== errors || $[23] !== moduleId || $[24] !== t) {
    t10 = {
      field: "pageTemplate",
      onRender: (value_2, onChange_2) => <TemplateSelector key={`${moduleId}-pageTemplate`} label={t("dashboard.course.lecture.select_template_title")} value={value_2} onChange={onChange_2} error={errors.fields.pageTemplate} data-test="module-create-lecture-pageTemplate" />
    };
    $[22] = errors;
    $[23] = moduleId;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t11 = [t7, t8, t9, t10];
    $[26] = t10;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = ["name", "pageTemplate"];
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== onCreate || $[33] !== opened || $[34] !== t11 || $[35] !== t5 || $[36] !== t6) {
    t13 = <>{t4}<CreateModal opened={opened} onClose={onClose} afterLeave={afterLeave} defaultValues={defaultValues} fullCaption={t5} caption={t6} creatable={t11} onCreate={onCreate} requiredFields={t12} dataTestKey="module-create-lecture" /></>;
    $[32] = onCreate;
    $[33] = opened;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};
type TemplateSelectorProps = FieldErrorAndDescriptionProps & FieldLabelProps & {
  value?: LectureTemplateInterface['id'];
  onChange: (id: LectureTemplateInterface['id']) => void;
};
const TemplateSelector = t0 => {
  const $ = _c(17);
  const {
    value,
    onChange,
    error,
    label,
    required: t1
  } = t0;
  const required = t1 === undefined ? true : t1;
  const {
    t
  } = useLocoTranslation();
  const {
    data: templates
  } = useLectureTemplate();
  let t2;
  if ($[0] !== onChange || $[1] !== templates) {
    t2 = () => {
      if (templates && templates[0]) {
        onChange(templates[0].id);
      }
    };
    $[0] = onChange;
    $[1] = templates;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== templates) {
    t3 = [templates];
    $[3] = templates;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== label || $[6] !== onChange || $[7] !== required || $[8] !== t || $[9] !== templates || $[10] !== value) {
    t4 = templates ? templates.length === 0 ? <span className="flex justify-center">{t("dashboard.course.lecture.templates_not_found")}</span> : <div className="flex flex-col gap-1"><FieldLabel label={label} required={required} /><div className="overflow-auto"><ul className="flex flex-col flex-wrap items-center justify-center gap-5 pb-5 sm:flex-row">{templates?.map(template => <TemplateCard key={template.id} onSelect={() => onChange(template.id)} isActive={template.id === value} preview={{
            href: `/dashboard/page/template/${template.id}/show`,
            dataTestId: "module-create-lecture-showTemplate"
          }}><img src={template.previewImage || ""} className="absolute" /></TemplateCard>)}</ul></div></div> : <div className="flex justify-center"><Loader /></div>;
    $[5] = label;
    $[6] = onChange;
    $[7] = required;
    $[8] = t;
    $[9] = templates;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== error) {
    t5 = <FieldErrorAndDescription error={error} errorClassName="text-center" />;
    $[12] = error;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
export default memo(CreateLectureModal);